import { SxProps, Theme } from "@mui/material";
import { dt, accentGradientDark, accentGradientDarkHover } from "../../../themes/redesignTokens";

// ═══════════════════════════════════════════════════
//  Shared sx constants extracted from:
//    - CreateServiceReportPage (SR)
//    - CreateWorkOrderPage (WO)
//    - StepMachineAndDetails / EditServiceRequestPage (RFS)
// ═══════════════════════════════════════════════════

/** TextField sx — merged from WO textFieldSx + SR/RFS focus styling */
export const textFieldSx: SxProps<Theme> = {
  "& .MuiOutlinedInput-root": {
    fontFamily: dt.font,
    fontSize: "14px",
    borderRadius: dt.radiusSm,
    backgroundColor: dt.inputBg,
    "& fieldset": { borderColor: dt.cardBorder },
    "&:hover fieldset": { borderColor: dt.blue },
    "&.Mui-focused fieldset": { borderColor: dt.blue },
  },
};

/** Section card container — from SR/RFS sectionSx (identical in both) */
export const sectionCardSx: SxProps<Theme> = {
  backgroundColor: dt.cardBg,
  borderRadius: dt.radius,
  border: `1px solid ${dt.cardBorder}`,
  boxShadow: dt.cardShadow,
  p: { xs: 2.5, md: 4 },
  display: "flex",
  flexDirection: "column",
  gap: 3,
};

/** Field label — from SR/RFS labelSx (13px, 600 weight, textPrimary) */
export const labelSx: SxProps<Theme> = {
  fontFamily: dt.font,
  fontSize: "13px",
  fontWeight: 600,
  color: dt.textPrimary,
  letterSpacing: "0.1px",
  mb: 0.5,
};

/** Heading — from RFS headingSx (larger heading style) */
export const headingSx: SxProps<Theme> = {
  fontFamily: dt.font,
  fontSize: { xs: "16px", md: "18px" },
  fontWeight: 700,
  color: dt.textPrimary,
  letterSpacing: "0.1px",
  mb: 1,
};

/** Section header row — from SR sectionHeaderSx (flex row with icon + label) */
export const sectionHeaderSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 1,
};

/** Section label text — from SR sectionLabelSx (uppercase 12px) */
export const sectionLabelSx: SxProps<Theme> = {
  fontFamily: dt.font,
  fontSize: "12px",
  fontWeight: 700,
  color: dt.textSecondary,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

/** Section icon — from SR sectionIconSx */
export const sectionIconSx: SxProps<Theme> = {
  fontSize: 16,
  color: dt.textMuted,
};

/** Textarea wrapper box — from SR/RFS raw textarea pattern */
export const textareaSx: SxProps<Theme> = {
  background: dt.inputBg,
  borderRadius: dt.radiusSm,
  border: "1.5px solid transparent",
  padding: "12px",
  transition: "all 0.2s ease",
  "&:focus-within": {
    border: `1.5px solid ${dt.blue}`,
    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
  },
};

/** Raw textarea inline styles — from SR/RFS (proven production pattern) */
export const textareaInnerStyle: React.CSSProperties = {
  border: "none",
  outline: "none",
  width: "100%",
  fontSize: "14px",
  fontFamily: "var(--font-sans)",
  color: "#1a1a2e",
  background: "transparent",
  resize: "vertical",
  boxSizing: "border-box",
};

/** File drop zone sx — from SR/RFS attachments section */
export const dropZoneSx = (opts: {
  isDragOver: boolean;
  hasError: boolean;
  height?: string;
}): SxProps<Theme> => ({
  height: opts.height || "100px",
  border: `2px dashed ${opts.hasError ? "#C0392B" : opts.isDragOver ? dt.blue : dt.cardBorder}`,
  borderRadius: dt.radiusSm,
  backgroundColor: opts.isDragOver ? dt.blueGlow : dt.inputBg,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": { borderColor: dt.blue, backgroundColor: dt.blueSoft },
});

/** Gradient submit button — combined from WO + SR action bars */
export const gradientSubmitSx: SxProps<Theme> = {
  fontFamily: dt.font,
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: dt.radiusSm,
  padding: "8px 24px",
  background: accentGradientDark,
  color: "#fff",
  boxShadow: "0 2px 8px rgba(52,152,219,0.18)",
  "&:hover": { background: accentGradientDarkHover },
  "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF" },
};
